import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/pfc";
import { Check, Package, Mail } from "lucide-react";

export default function MarketplaceOrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("pending");
  const [tries, setTries] = useState(0);

  useEffect(() => {
    document.title = "Order Confirmed — PFC Marketplace";
    if (!sessionId) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get(`/marketplace/order-status/${sessionId}`);
        if (cancelled) return;
        setStatus(data.payment_status);
        if (data.order) setOrder(data.order);
        if (data.payment_status !== "paid" && tries < 8) {
          setTimeout(() => setTries((t) => t + 1), 2000);
        }
      } catch {
        if (tries < 8) setTimeout(() => setTries((t) => t + 1), 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, tries]);

  return (
    <div className="bg-ink-900 min-h-screen text-white py-20 px-5" data-testid="order-success">
      <div className="max-w-xl mx-auto text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-pfc-gold/15 border border-pfc-gold rounded-full mb-6">
          <Check size={28} className="text-pfc-gold" />
        </div>
        <h1 className="font-display text-3xl sm:text-4xl mb-4">
          {status === "paid" ? "Order Confirmed" : "Processing your order…"}
        </h1>
        {order && (
          <div className="border border-white/10 bg-ink-800/40 p-6 mt-6 text-left">
            <div className="flex justify-between mb-3">
              <span className="text-white/60 text-xs uppercase tracking-[0.3em] font-sub">Order ID</span>
              <span className="text-white text-sm">{order.order_id}</span>
            </div>
            <div className="flex justify-between mb-3">
              <span className="text-white/60 text-xs uppercase tracking-[0.3em] font-sub">Product</span>
              <span className="text-white text-sm">{order.product_title} × {order.quantity}</span>
            </div>
            <div className="flex justify-between mb-3">
              <span className="text-white/60 text-xs uppercase tracking-[0.3em] font-sub">Vendor</span>
              <span className="text-white text-sm">{order.vendor_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/60 text-xs uppercase tracking-[0.3em] font-sub">Total</span>
              <span className="text-pfc-gold font-display text-lg">${order.amount?.toFixed(2)}</span>
            </div>
          </div>
        )}
        <div className="mt-8 text-white/70 text-sm space-y-3">
          <p className="inline-flex items-center gap-2 justify-center"><Mail size={14} className="text-pfc-gold" /> A confirmation will be sent to PFC and your vendor.</p>
          <p className="inline-flex items-center gap-2 justify-center"><Package size={14} className="text-pfc-gold" /> Your vendor will reach out with shipping updates.</p>
        </div>
        <div className="mt-10 flex gap-3 justify-center">
          <Link to="/marketplace" className="px-6 py-3 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-xs hover:bg-pfc-gold-bright">
            Continue Shopping
          </Link>
          <Link to="/" className="px-6 py-3 border border-white/20 text-white font-sub uppercase tracking-[0.25em] text-xs hover:bg-white hover:text-ink-900">
            Home
          </Link>
        </div>
      </div>
    </div>
  );
}
